import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { extractError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Mail, Send, Sparkles, Clock } from "lucide-react";
import { toast } from "sonner";

export default function NotificationSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["notification-prefs"],
    queryFn: () => api.get("/notifications/preferences").then((r) => r.data),
  });
  const [local, setLocal] = useState(null);

  useEffect(() => {
    if (data?.prefs) setLocal(data.prefs);
  }, [data]);

  const save = useMutation({
    mutationFn: (payload) => api.patch("/notifications/preferences", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notification-prefs"] });
      toast.success("Préférences enregistrées");
    },
    onError: (e) => toast.error(extractError(e, "Erreur")),
  });

  const test = useMutation({
    mutationFn: () => api.post("/notifications/test-email"),
    onSuccess: (r) => toast.success(`Email test envoyé à ${r.data.sent_to}`),
    onError: (e) => toast.error(extractError(e, "Envoi impossible — vérifie que ton adresse est validée sur resend.com")),
  });

  if (isLoading || !local || !data) return <div className="text-slate-500">Chargement…</div>;

  const toggleContent = (key) => {
    const next = local.content_types.includes(key)
      ? local.content_types.filter((x) => x !== key)
      : [...local.content_types, key];
    setLocal({ ...local, content_types: next });
  };
  const toggleEvent = (key) => {
    const next = local.immediate_events.includes(key)
      ? local.immediate_events.filter((x) => x !== key)
      : [...local.immediate_events, key];
    setLocal({ ...local, immediate_events: next });
  };

  const freqLabels = { daily: "Quotidien", weekly: "Hebdomadaire (lundi)", monthly: "Mensuel (1er du mois)" };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Profil</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Notifications email</h1>
        <p className="text-sm text-slate-500 mt-1.5">Configure quand et à propos de quoi tu reçois des emails.</p>
      </div>

      {/* Master switch */}
      <Card className="p-5 flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#023E7315", color: "#023E73" }}><Bell size={20} /></div>
        <div className="flex-1">
          <div className="font-display font-bold text-slate-900">Notifications activées</div>
          <div className="text-xs text-slate-500 mt-0.5">Désactive pour ne plus recevoir aucun email de la plateforme.</div>
        </div>
        <Switch data-testid="pref-enabled" checked={local.enabled} onCheckedChange={(v) => setLocal({ ...local, enabled: v })} />
      </Card>

      {/* Immediate events */}
      <Card className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "#023E7315", color: "#023E73" }}><Sparkles size={16} /></div>
          <div>
            <div className="font-display font-bold text-slate-900">Événements immédiats</div>
            <div className="text-xs text-slate-500">Envoyés dès que l&apos;événement se produit.</div>
          </div>
        </div>
        <div className="space-y-2">
          {Object.entries(data.events).map(([key, label]) => (
            <label key={key} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50/60 rounded px-2 -mx-2 transition">
              <input
                type="checkbox"
                data-testid={`event-${key}`}
                checked={local.immediate_events.includes(key)}
                onChange={() => toggleEvent(key)}
                disabled={!local.enabled}
                className="w-4 h-4 rounded border-slate-300 text-[#023E73]"
              />
              <span className="text-sm text-slate-900">{label}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* Recap frequency + content */}
      <Card className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "#023E7315", color: "#023E73" }}><Clock size={16} /></div>
          <div>
            <div className="font-display font-bold text-slate-900">Récap régulier</div>
            <div className="text-xs text-slate-500">Un résumé des actualités importantes envoyé automatiquement.</div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label>Fréquence</Label>
            <Select value={local.frequency} onValueChange={(v) => setLocal({ ...local, frequency: v })} disabled={!local.enabled}>
              <SelectTrigger data-testid="pref-frequency" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {data.frequencies.map((f) => <SelectItem key={f} value={f}>{freqLabels[f] || f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Contenu du récap</div>
        <div className="space-y-2">
          {Object.entries(data.content_labels).map(([key, label]) => (
            <label key={key} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50/60 rounded px-2 -mx-2 transition">
              <input
                type="checkbox"
                data-testid={`content-${key}`}
                checked={local.content_types.includes(key)}
                onChange={() => toggleContent(key)}
                disabled={!local.enabled}
                className="w-4 h-4 rounded border-slate-300 text-[#023E73]"
              />
              <span className="text-sm text-slate-900">{label}</span>
            </label>
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <Button data-testid="save-prefs" onClick={() => save.mutate(local)} disabled={save.isPending} className="text-white" style={{ background: "#023E73" }}>
          <Mail size={14} className="mr-1.5" /> {save.isPending ? "Enregistrement…" : "Enregistrer les préférences"}
        </Button>
        <Button data-testid="test-email" variant="outline" onClick={() => test.mutate()} disabled={test.isPending}>
          <Send size={14} className="mr-1.5" /> {test.isPending ? "Envoi…" : "M'envoyer un email de test"}
        </Button>
      </div>
    </div>
  );
}
